/**
 * Shared copy and constants for the Extended Network Finance marketing page.
 * Partner logos / official hero art are placeholders until assets are available.
 */

export const HEADER_CONTENT = {
  headline: 'Extended Network Finance',
  subheadline: 'Sell receivables from customers outside C2FO’s platform',
  // SLED-87: restore when J.P. Morgan signoff lands.
  // 'Access working capital from approved invoices from customers outside C2FO’s network.  Powered by J.P. Morgan and Mondu.',
  supportingLine:
    'Access working capital from approved invoices from customers outside C2FO’s network.  Powered by Mondu.',
  ctaLabel: 'Apply With Mondu',
} as const;

export const ACCELERATE_ADDITIONAL_RECEIVABLES_CONTENT = {
  title: 'Accelerate Additional Receivables',
  intro:
    "If you're funding invoices on C2FO today, chances are that's only part of your accounts receivable. The rest may be on long payment terms or being financed elsewhere at a higher cost.",
  trustBanner: {
    title: 'Built On The Early Pay You Trust',
    description:
      "This program extends the trust you've already built with C2FO to more of your receivables, giving you access to working capital across more of your customer base.",
  },
  benefits: [
    {
      title: 'Immediate Cash Flow',
      description:
        'Sell customer-approved or customer-unapproved invoices and access funds as soon as Mondu approves—no more waiting 30, 60, or 90 days.',
    },
    {
      title: 'Keep Customer Relationships',
      description:
        'Qualified suppliers* will continue receiving payments into their existing bank account with no changes to remittance.',
    },
    {
      title: 'On Demand',
      description: 'Choose which invoices and customers to sell - with no fixed commitments or minimum volumes.',
    },
  ],
  // Resolves the asterisk on the 'Keep Customer Relationships' benefit.
  benefitsFootnote: '*Qualification will be evaluated following data submission.',
} as const;

export const HOW_IT_WORKS_CONTENT = {
  title: 'How It Works',
  intro:
    'This program enables eligible businesses to access immediate working capital by selling approved invoices instead of waiting for customer payment terms to mature.',
  disclaimer:
    'J.P. Morgan has not entered into a legal partnership with any of the parties listed in this document to provide the products or services described herein. Third-party trademarks, brand names, and descriptions of products and services that appear herein are provided by the respective third party. J.P. Morgan is not liable or responsible for such trademarks, brand names, descriptions of products, companies and/or services. J.P. Morgan may generate a profit from the use of any services or products provided by these third parties. Nothing in this material shall be taken as an endorsement of any third party, its products or its services, or as advice on the suitability of the third party’s services for the client. You shall make an independent determination for selection of the services provided by the third parties. Neither J.P. Morgan nor its affiliates shall be liable to you for any loss or liability suffered by you arising from the use of the third party’s products or services.',
  partners: {
    jpMorgan: {
      eyebrow: 'Supported By',
      name: 'J.P. Morgan',
      buttonLabel: 'How Is J.P. Morgan Involved?',
      popover: [
        "The program is supported by a financing facility provided by J.P. Morgan, combining Mondu's technology-driven experience with the strength and scale of institutional funding.",
        'J.P. Morgan has not entered into a legal partnership with any of the parties listed on this page.',
      ],
    },
    mondu: {
      eyebrow: 'Powered By',
      name: 'Mondu',
      buttonLabel: 'Who Is Mondu?',
      popover:
        'Mondu provides the customer-facing experience for the program, acting as the underwriter and operator of a platform facilitating the purchase of eligible invoices responsible onboarding etc. ',
    },
  },
} as const;

export const ELIGIBILITY_CONTENT = {
  title: 'Eligibility Criteria',
  intro:
    'Extended Network Finance is designed for eligible businesses that sell on credit terms and want greater flexibility over cash flow. It’s ideal for suppliers, manufacturers, distributors, wholesalers, and other B2B businesses.',
  criteria: [
    {
      title: 'US-based suppliers',
      description: 'Headquartered and operates in the United States.',
    },
    {
      title: '1+ year in business',
      description: 'Operating for at least one full year.',
    },
    {
      title: '$5M+ annual revenue',
      description: 'At least $5 million in annual revenue.',
    },
  ],
  disclaimer:
    'Program availability, eligibility criteria, and contractual terms vary by jurisdiction and program structure. Final eligibility is confirmed by Mondu during underwriting.',
} as const;

export const GET_STARTED_CONTENT = {
  title: 'Get Started',
  steps: [
    {
      title: 'Apply With Mondu',
      description:
        'Get your business information ready, for example, registration number and business bank account details.',
    },
    {
      title: 'Receive A Decision',
      description: 'Mondu typically evaluates your eligibility in less than 24 hours.',
    },
    {
      title: 'Request Early Payments',
      description:
        'Choose eligible invoices from approved customers to sell through the program, and receive payment before invoice maturity.',
    },
  ],
} as const;

export const PRICING_PREVIEW_CONTENT = {
  title: 'Pricing Preview',
  rate: '0.74% – 1.00% per 30 days',
  subtitle: 'Discount on invoice value',
  description:
    'You sell your eligible invoices to Mondu and receive funds before your customer pays. In return, a discount is applied to the invoice value, based on the risk profile of your business and your customers and on the number of days until the invoice is due.',
  disclaimer:
    'Pricing ranges shown are directional only and subject to underwriting by J.P. Morgan and their service provider, Mondu. Not every customer will be eligible. Pricing ranges shown are directional only and are based on values you have provided.\u00a0Such pricing ranges are presented for illustrative and discussion purposes only, and do not constitute an offer, solicitation, advice or recommendation, to make any investment decisions or purchase any financial instruments, nor does this constitute a commitment by any J.P. Morgan entity to extend or arrange credit or to provide any other products or services, and may not be construed as such. The ultimate pricing ranges remain subject to underwriting by J.P. Morgan and Mondu. \u00a0J.P. Morgan may change pricing determined by J.P. Morgan at its sole discretion and subject to further terms and conditions, and such ranges may be changed by J.P. Morgan from time to time. Not all products and services are available in all geographic areas or to all customers. Eligibility for particular products and services is subject to final determination.',
  // Approved Mondu-only wording from the frame. The J.P. Morgan `disclaimer` key above is retained
  // unrendered as a restore target for when that branding returns to the marketing page. Nothing in
  // the wizard renders it.
  monduDisclaimer:
    'Pricing ranges shown are directional only and subject to underwriting by Mondu. Not every customer will be eligible.',
} as const;

export const CONTINUE_TO_MONDU_CONTENT = {
  headline: 'Ready to accelerate more of your receivables?',
  body: "Continue to Mondu to get your personalized quote and complete your application. They'll guide you through connecting your data and underwriting.",
  ctaLabel: 'Apply With Mondu',
} as const;

export const MONDU_HANDOFF_MODAL_CONTENT = {
  title: 'Off To Mondu!',
  disclaimer:
    "You are now leaving this site to go to Mondu, a third-party service provider's website. This link is provided for your convenience only. Mondu's site is governed by its own terms and privacy policy, which we encourage you to review before proceeding.",
  consent: {
    beforeLink: 'By clicking below, you agree that C2FO will share your personal data in accordance with the C2FO ',
    privacyPolicyLabel: 'Privacy Policy',
    afterLink: ' with Mondu to support your application.',
  },
  redirectNote: 'As soon as you are redirected, you will receive a code via email to log into the Mondu portal.',
  impersonationMessage: 'The supplier must continue to Mondu directly so they can provide consent.',
  requestError: {
    title: 'Unable to continue',
    description: 'Something went wrong. Please try again.',
  },
  cancelLabel: 'Cancel',
  proceedLabel: 'Proceed to Mondu',
} as const;

export const FAQS_CONTENT = {
  title: 'FAQs',
  // Permanent wording from the frame. Not part of the SLED-87 restore: it stays when J.P. Morgan returns.
  disclaimer:
    'C2FO is not a credit broker, nor does it provide, underwrite, price, or service any payout provided or facilitated by Mondu. C2FO may receive compensation from Mondu if you apply and receive a payout. This does not change your rates or fees.',
  items: [
    {
      question: 'Do I have to sell all of my invoices?',
      answer:
        'No. You choose which eligible customers and invoices to sell. There is no obligation to run all of your receivables through the program, and you can adjust which invoices you sell over time.',
    },
    {
      question: 'Will my customers be notified?',
      answer:
        'This depends entirely on the collection setup agreed upon before the contract is signed. In an undisclosed setup, no buyers will be notified or contacted. In a disclosed setup, buyers whose invoices you sell may receive a professional Notice of Assignment directing them to pay into the designated collection account. Mondu handles all communication professionally, and invoice purchasing remains a routine business practice that large buyers handle every day.',
    },
    {
      question: 'How is pricing determined?',
      answer:
        "Pricing is based on factors such as credit strength for you and your buyer customers, invoice payment terms, and payout volume. You'll see indicative rates when you request a quote, and final pricing is confirmed by Mondu during underwriting — before you accept any payout.",
    },
    {
      question: 'Will lien releases be required from my existing lenders?',
      // SLED-87: restore when J.P. Morgan signoff lands.
      // 'If you currently use your invoices as collateral with an existing lender, J.P. Morgan and Mondu may require your existing lender to release its legal claim on the invoices bought through this program. In some cases, an agreement with your existing lender and Mondu may be needed instead. Mondu will explain what’s required during the application process and, in many cases, can work directly with your existing lender.',
      answer:
        'If you currently use your invoices as collateral with an existing lender, Mondu may require your existing lender to release its legal claim on the invoices bought through this program. In some cases, an agreement with your existing lender and Mondu may be needed instead. Mondu will explain what’s required during the application process and, in many cases, can work directly with your existing lender.',
    },
    {
      question: 'Will I need to use a separate account for payments?',
      answer:
        'This depends on the specific setup offered to you, which is decided and agreed upon before your contract is signed. If you are on a disclosed setup, payments for the purchased invoices will need to be made into a designated collection account. If you are on an undisclosed setup, there is no change to where your buyers send payments; however, a standard cash dominion arrangement will be set up on the account, which will allow Mondu to monitor incoming payments and confirm repayment. In all cases, this only applies to the specific invoices you choose to sell - it never gives the purchaser control over your other business accounts or overall cash flow.',
    },
  ],
} as const;
